'use client';

import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Menu,
  Stack,
  Button,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';

import {
  getPostLinkFromPost,
  getPostRawLinkFromPost,
  addPostLinksToCampaignForcedAdditions,
} from 'src/utils/campaign/campaign-forced-addition-client';
import { toUserErrorMessage } from 'src/utils/error-message';

import { RADIUS, SHADOW } from 'src/theme/ui-tokens';
import { fsCall } from 'src/actions/firestore/dispatch';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { useDemoActionGuard } from 'src/sections/common/demo/use-demo-guard';

import { useAuthContext } from 'src/auth/hooks';


const DEMO_CAMPAIGN_ADD_MSG = '데모에서는 캠페인에 추가할 수 없어요';

const CAMPAIGN_ADD_FAILED_MESSAGE = '캠페인에 추가하지 못했습니다. 잠시 뒤 다시 시도해 주세요.';

/**
 * @param {'postCard'|'postList'|'postTable'} placement
 */
type CampaignRow = { id?: unknown; name?: string; title?: string } & Record<string, unknown>

export default function PostCardCampaignOverlay({
  post,
  placement = 'postCard',
  buttonVariant = 'soft',
  showLabel = false,
  sx,
}: {
  post: Record<string, unknown>
  placement?: 'postCard' | 'postList' | 'postTable'
  buttonVariant?: 'soft' | 'contained'
  showLabel?: boolean
  sx?: Record<string, unknown>
}) {
  const { user } = useAuthContext();
  const workspaceId = user?.lastWorkspace ?? '';
  const canUseCampaign = Boolean(workspaceId?.trim());
  const { wrap } = useDemoActionGuard();

  const postLink = getPostLinkFromPost(post);
  // 캠페인에 담을 때는 원문을 넘긴다 — 정규화는 담는 쪽이 하고 원문은 원문 지도에 남는다
  const postRawLink = getPostRawLinkFromPost(post);
  const isDisabled = !postLink;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [savingCampaignId, setSavingCampaignId] = useState<string | null>(null);
  const [addedCampaignIds, setAddedCampaignIds] = useState<Set<string>>(() => new Set());

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    if (isDisabled) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  }, [isDisabled]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setAddedCampaignIds(new Set());
  }, []);

  useEffect(() => {
    if (!open || !canUseCampaign) return undefined;

    let cancelled = false;
    const loadCampaigns = async () => {
      setLoadingCampaigns(true);
      try {
        const result = await fsCall('campaign.byWorkspace.get', { workspaceId });
        if (cancelled) return;
        const rows: CampaignRow[] = Array.isArray(result?.rows) ? (result.rows as CampaignRow[]) : [];
        const sorted = [...rows].sort((a, b) => {
          const nameA = String(a?.name || a?.title || '').trim();
          const nameB = String(b?.name || b?.title || '').trim();
          return nameA.localeCompare(nameB, 'ko');
        });
        setCampaigns(sorted);
      } catch {
        if (!cancelled) setCampaigns([]);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    };

    void loadCampaigns();
    return () => {
      cancelled = true;
    };
  }, [open, canUseCampaign, workspaceId]);

  const handleSelectCampaign = useCallback(
    async (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, campaignId: unknown) => {
      e.stopPropagation();
      const id = String(campaignId || '').trim();
      if (!id || !postLink || savingCampaignId) return;

      setSavingCampaignId(id);
      try {
        // ⚠️ 세 번째 인자는 수집 요청 큐가 소속을 묻는 대상이다 — 워크스페이스를 반드시 넘긴다
        const result = await addPostLinksToCampaignForcedAdditions(id, [postRawLink || postLink], workspaceId) as { ok?: boolean; requestedCount?: number; error?: { message?: string } };
        if (!result?.ok) {
          toast.error(toUserErrorMessage(result?.error, CAMPAIGN_ADD_FAILED_MESSAGE));
          return;
        }

        setAddedCampaignIds((prev) => new Set(prev).add(id));

        if ((result.requestedCount ?? 0) > 0) {
          toast.success(
            `캠페인에 추가되었습니다. (수집 요청 ${result.requestedCount}건)`
          );
        } else {
          toast.success('캠페인에 추가되었습니다.');
        }
      } catch (err) {
        toast.error(toUserErrorMessage(err, CAMPAIGN_ADD_FAILED_MESSAGE));
      } finally {
        setSavingCampaignId(null);
      }
    },
    [postLink, postRawLink, savingCampaignId, workspaceId]
  );

  const isContainedTrigger = placement === 'postCard' && buttonVariant === 'contained';
  const tooltipTitle = isDisabled
    ? '링크가 없는 포스팅은 캠페인에 추가할 수 없습니다.'
    : '캠페인에 추가';

  const triggerButton = (
    <Button
      className="campaign-overlay-trigger"
      size="small"
      disabled={isDisabled}
      onClick={handleOpen}
      color="primary"
      variant={isContainedTrigger ? 'contained' : 'soft'}
      aria-label="캠페인에 추가"
      sx={{
        borderRadius: RADIUS.sm,
        py: 0.1,
        ...(showLabel ? { pr: 1, pl: 0.7 } : { px: 0.5 }),
        minWidth: 0,
        minHeight: 0,
        height: isContainedTrigger ? '24px' : '22px',
        fontSize: '11px',
        ...(isDisabled ? { opacity: 0.5 } : {}),
        ...(isContainedTrigger
          ? {
            boxShadow: SHADOW.raised,
            '&:hover': { boxShadow: SHADOW.overlay },
          }
          : {}),
      }}
    >
      <Iconify
        icon="solar:star-linear"
        width={14}
        sx={{ mr: showLabel ? 0.3 : 0 }}
      />
      {showLabel && '캠페인'}
    </Button>
  );

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          ...sx,
        }}
      >
        <Tooltip title={tooltipTitle}>
          <span>{triggerButton}</span>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          root: { sx: { zIndex: 13050 } },
          paper: {
            sx: {
              mt: 0.5,
              zIndex: 13050,
              minWidth: 280,
              maxWidth: 360,
              overflow: 'hidden',
              p: 0,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: SHADOW.overlay,
            },
          },
        }}
        keepMounted={false}
      >
        <Box
          sx={{ p: 1.5 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            캠페인 선택
          </Typography>

          {!canUseCampaign && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              워크스페이스 선택 후 캠페인에 추가할 수 있습니다.
            </Typography>
          )}

          <Stack
            spacing={0}
            sx={{
              maxHeight: 240,
              overflowY: 'auto',
              bgcolor: 'grey.50',
              borderRadius: RADIUS.sm,
            }}
          >
            {loadingCampaigns ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : campaigns.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 1.5, py: 1.5 }}
              >
                등록된 캠페인이 없습니다.
              </Typography>
            ) : (
              campaigns.map((campaign) => {
                const cid = campaign.id as string;
                const label = campaign.name || campaign.title || cid;
                const isSaving = savingCampaignId === cid;
                const isAdded = addedCampaignIds.has(cid);
                return (
                  <Box
                    key={cid}
                    role="button"
                    tabIndex={0}
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      wrap(() => handleSelectCampaign(e, cid), DEMO_CAMPAIGN_ADD_MSG)()
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                      e.stopPropagation();
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        wrap(
                          () => handleSelectCampaign(e, cid),
                          DEMO_CAMPAIGN_ADD_MSG
                        )();
                      }
                    }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor:
                        canUseCampaign && postLink && !isSaving
                          ? 'pointer'
                          : 'default',
                      opacity: canUseCampaign && postLink && !isSaving ? 1 : 0.6,
                      ...(isAdded && {
                        bgcolor: 'primary.lighter',
                        color: 'primary.darker',
                      }),
                      '&:hover': {
                        bgcolor:
                          canUseCampaign && postLink && !isSaving
                            ? isAdded
                              ? 'primary.light'
                              : 'action.hover'
                            : undefined,
                      },
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {label}
                    </Typography>
                    {isSaving && <CircularProgress size={16} />}
                    {!isSaving && isAdded && (
                      <Iconify
                        icon="solar:check-read-linear"
                        width={16}
                        sx={{ color: 'primary.main', flexShrink: 0 }}
                      />
                    )}
                  </Box>
                );
              })
            )}
          </Stack>
        </Box>
      </Menu>
    </>
  );
}

PostCardCampaignOverlay.propTypes = {
  post: PropTypes.object.isRequired,
  placement: PropTypes.oneOf(['postCard', 'postList', 'postTable']),
  buttonVariant: PropTypes.oneOf(['soft', 'contained']),
  showLabel: PropTypes.bool,
  sx: PropTypes.object,
};
